// 家用面板 P2 / P3 卡片（docs/panel 05-ui-ux）：V-03 相簿、V-04 輪播與顯示設定。
// 相簿：主人與有「可上傳照片」的分享者可上傳；主人刪任何一張、分享者只刪自己上傳的；都能排序（SD §3 v1.3）。
// 顯示設定：只有主人能改（SD §4）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import {
  DEFAULT_DISPLAY, MAX_PHOTO_BYTES, canDeletePhoto, commitPanelPhotos, currentUserId, deletePanelPhoto, getPanelAlbum,
  getPanelSettings, movePhoto, orderPanelPhotos, panelPhotoLongEdge, parseDisplaySettings, photoRemaining, preparePanelPhotos,
  putPanelDisplay, resolvePanelUrl, toPanelJpeg, uploadPanelPhoto, validateDisplay,
  type PanelAlbum, type PanelDisplaySettings,
} from './panels';

/** V-03 相簿 */
export function PanelAlbumCard({ panelId, isOwner, canUpload }: { panelId: string; isOwner: boolean; canUpload: boolean }) {
  const { t } = useTranslation();
  const [album, setAlbum] = useState<PanelAlbum | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const me = currentUserId();

  const load = useCallback(() => {
    getPanelAlbum(panelId).then(setAlbum).catch(() => setMsg(t('panels.errGeneric')));
  }, [panelId, t]);
  useEffect(load, [load]);

  const remaining = photoRemaining(album);

  const upload = async (files: File[]) => {
    if (!files.length) return;
    setMsg(null);
    if (files.length > remaining) {
      setMsg(remaining === 0 ? t('panels.album.full') : t('panels.album.tooMany', { count: remaining }));
      files = files.slice(0, remaining);
      if (!files.length) return;
    }
    setProgress({ done: 0, total: files.length });
    let failed = 0;
    try {
      const edge = await panelPhotoLongEdge(panelId);
      const blobs: Blob[] = [];
      for (const f of files) {
        try {
          const b = await toPanelJpeg(f, edge);
          if (b.size > MAX_PHOTO_BYTES) throw new Error('too big');
          blobs.push(b);
        } catch {
          failed++;
        }
      }
      if (!blobs.length) {
        setMsg(t('panels.album.badFormat'));
        return;
      }
      const ups = await preparePanelPhotos(panelId, blobs.map((b) => ({ content_type: 'image/jpeg', bytes: b.size })));
      const okIds: string[] = [];
      for (let i = 0; i < ups.length; i++) {
        try {
          await uploadPanelPhoto(ups[i].upload_url, blobs[i], 'image/jpeg');
          okIds.push(ups[i].photo_id);
        } catch {
          failed++;
        }
        setProgress({ done: i + 1, total: ups.length });
      }
      if (okIds.length) {
        const res = await commitPanelPhotos(panelId, okIds);
        failed += res.failed.length;
      }
      if (failed) setMsg(t('panels.album.someFailed', { count: failed }));
    } catch (e) {
      setMsg(e instanceof ApiError && e.code === 'QUOTA_EXCEEDED' ? t('panels.album.full') : t('panels.album.uploadFailed'));
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    }
  };

  const remove = async (photoId: string) => {
    setMsg(null);
    try {
      await deletePanelPhoto(panelId, photoId);
    } catch {
      setMsg(t('panels.errGeneric'));
    }
    load();
  };

  const reorder = async (from: number, to: number) => {
    if (!album) return;
    const next = movePhoto(album.photos, from, to);
    if (next === album.photos) return;
    setAlbum({ ...album, photos: next });
    try {
      await orderPanelPhotos(panelId, next.map((p) => p.photo_id));
    } catch {
      setMsg(t('panels.errGeneric'));
      load();
    }
  };

  return (
    <div className="card" data-testid="panel-album">
      <h2 style={{ marginTop: 0 }}>{t('panels.album.title')}</h2>
      <p className="text-secondary" data-testid="panel-album-remaining">
        {remaining === 0 ? t('panels.album.full')
          : album?.count ? t('panels.album.countRemaining', { n: album.count, count: remaining })
          : t('panels.album.remaining', { count: remaining })}
      </p>
      {canUpload ? (
        <div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/heic,image/heif" multiple hidden
            data-testid="panel-album-file" onChange={(e) => void upload(Array.from(e.target.files ?? []))} />
          <button className="btn-primary" disabled={!!progress || remaining === 0} onClick={() => fileRef.current?.click()}>
            {t('panels.album.upload')}
          </button>
          {progress && <span className="text-secondary" style={{ marginLeft: 12 }}>{t('panels.album.uploading', progress)}</span>}
        </div>
      ) : (
        <p className="text-secondary">{t('panels.album.noPermission')}</p>
      )}
      {msg && <p className="error-text" role="alert">{msg}</p>}
      <ul className="dv-album-grid" style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
        {(album?.photos ?? []).map((p, i) => (
          <li key={p.photo_id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (drag !== null) void reorder(drag, i); setDrag(null); }}
            data-testid="panel-album-item" style={{ position: 'relative' }}>
            <img src={resolvePanelUrl(p.url)} alt={t('panels.album.photoAlt', { n: i + 1 })} loading="lazy"
              style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 6 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>
                <button className="btn-ghost" aria-label={t('panels.album.moveUp')} disabled={i === 0} onClick={() => void reorder(i, i - 1)}>←</button>
                <button className="btn-ghost" aria-label={t('panels.album.moveDown')} disabled={i === (album?.photos.length ?? 0) - 1}
                  onClick={() => void reorder(i, i + 1)}>→</button>
              </span>
              {canDeletePhoto(isOwner, p, me) && (
                <button className="btn-ghost" onClick={() => void remove(p.photo_id)}>{t('panels.album.delete')}</button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** V-04 輪播與顯示設定（只限主人） */
export function PanelDisplayCard({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const [s, setS] = useState<PanelDisplaySettings>(DEFAULT_DISPLAY);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPanelSettings(panelId).then((r) => { setS(parseDisplaySettings(r)); setLoaded(true); }).catch(() => setMsg(t('panels.errGeneric')));
  }, [panelId, t]);

  const save = async () => {
    setMsg(null);
    if (validateDisplay(s).length) {
      setMsg(t('panels.disp.invalid'));
      return;
    }
    setBusy(true);
    try {
      setS(parseDisplaySettings(await putPanelDisplay(panelId, s)));
      setMsg(t('panels.disp.saved'));
    } catch {
      setMsg(t('panels.errGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const time = (v: string, on: (x: string) => void, label: string) => (
    <input type="time" value={v} aria-label={label} onChange={(e) => on(e.target.value)} />
  );

  return (
    <div className="card" data-testid="panel-display">
      <h2 style={{ marginTop: 0 }}>{t('panels.disp.title')}</h2>
      {loaded && (
        <div style={{ display: 'grid', gap: 10 }}>
          <label>{t('panels.disp.interval')}{' '}
            <select value={s.interval_s} onChange={(e) => setS({ ...s, interval_s: Number(e.target.value) })} data-testid="panel-disp-interval">
              {[10, 15, 30, 60, 300, 900, 3600].map((n) => (
                <option key={n} value={n}>{n < 60 ? t('panels.disp.seconds', { count: n }) : t('panels.disp.minutes', { count: n / 60 })}</option>
              ))}
            </select>
          </label>
          <label>{t('panels.disp.order')}{' '}
            <select value={s.order} onChange={(e) => setS({ ...s, order: e.target.value as PanelDisplaySettings['order'] })}>
              <option value="sequential">{t('panels.disp.sequential')}</option>
              <option value="shuffle">{t('panels.disp.shuffle')}</option>
            </select>
          </label>
          <label>{t('panels.disp.transition')}{' '}
            <select value={s.transition} onChange={(e) => setS({ ...s, transition: e.target.value as PanelDisplaySettings['transition'] })}>
              <option value="fade">{t('panels.disp.fade')}</option>
              <option value="none">{t('panels.disp.none')}</option>
            </select>
          </label>
          <label><input type="checkbox" checked={s.clock.on} onChange={(e) => setS({ ...s, clock: { on: e.target.checked } })} /> {t('panels.disp.clock')}</label>
          <fieldset>
            <legend><label><input type="checkbox" checked={s.sleep.on} onChange={(e) => setS({ ...s, sleep: { ...s.sleep, on: e.target.checked } })} /> {t('panels.disp.sleep')}</label></legend>
            {time(s.sleep.from, (x) => setS({ ...s, sleep: { ...s.sleep, from: x } }), t('panels.disp.from'))} –{' '}
            {time(s.sleep.to, (x) => setS({ ...s, sleep: { ...s.sleep, to: x } }), t('panels.disp.to'))}
            <label style={{ marginLeft: 12 }}>{t('panels.disp.tapWake')}{' '}
              <select value={s.sleep.tap_wake_s} onChange={(e) => setS({ ...s, sleep: { ...s.sleep, tap_wake_s: Number(e.target.value) } })}>
                {[10, 30, 60, 120, 300].map((n) => <option key={n} value={n}>{t('panels.disp.seconds', { count: n })}</option>)}
              </select>
            </label>
          </fieldset>
          <fieldset>
            <legend><label><input type="checkbox" checked={s.night.on} onChange={(e) => setS({ ...s, night: { ...s.night, on: e.target.checked } })} /> {t('panels.disp.night')}</label></legend>
            {time(s.night.from, (x) => setS({ ...s, night: { ...s.night, from: x } }), t('panels.disp.from'))} –{' '}
            {time(s.night.to, (x) => setS({ ...s, night: { ...s.night, to: x } }), t('panels.disp.to'))}
            <label style={{ display: 'block' }}>{t('panels.disp.nightVolume')}{' '}
              <input type="range" min={0.1} max={1} step={0.1} value={s.night.ring_volume} data-testid="panel-disp-night-volume"
                onChange={(e) => setS({ ...s, night: { ...s.night, ring_volume: Number(e.target.value) } })} /> {Math.round(s.night.ring_volume * 100)}%
            </label>
            <label style={{ display: 'block' }}><input type="checkbox" checked={!s.night.ring}
              onChange={(e) => setS({ ...s, night: { ...s.night, ring: !e.target.checked } })} /> {t('panels.disp.nightSilent')}</label>
            <label style={{ display: 'block' }}><input type="checkbox" checked={!s.night.photos}
              onChange={(e) => setS({ ...s, night: { ...s.night, photos: !e.target.checked } })} /> {t('panels.disp.nightClockOnly')}</label>
            <p className="text-secondary" style={{ margin: 0 }}>{t('panels.disp.nightNote')}</p>
          </fieldset>
          <label><input type="checkbox" checked={s.ring} onChange={(e) => setS({ ...s, ring: e.target.checked })} /> {t('panels.disp.ring')}</label>
          <div><button className="btn-primary" disabled={busy} onClick={() => void save()} data-testid="panel-disp-save">{t('panels.disp.save')}</button></div>
        </div>
      )}
      {msg && <p className="text-secondary" role="status">{msg}</p>}
    </div>
  );
}
